import axios from '../axiosInstance';
import { createCrudApi } from '../createCrudApi';

export type LabRequestsGetParams = {
  autopsy_protocol?: number;
  is_sent?: boolean;
  limit?: number;
  offset?: number;
};

type LabRequestBase = {
  id: number;
  autopsy_protocol: number;
  examination_type: number;
  examination_type_code: string;
  created_by: number | null;
  created_by_name: string;
  toxicologist: number | null;
  toxicologist_name: string;
  last_printed_at: string | null;
  sent_at: string | null;
  updated?: boolean;
};

type LabRequestBaseFormData = Pick<
  LabRequestBase,
  'autopsy_protocol' | 'examination_type' | 'created_by' | 'toxicologist'
>;

// ---------------------------------------------------------------------------
// CT & RTG
// ---------------------------------------------------------------------------

export type CtRequest = LabRequestBase & { request_text: string | null };
export type RtgRequest = LabRequestBase & { request_text: string | null };

export type CtRequestFormData = LabRequestBaseFormData & { request_text?: string | null };
export type RtgRequestFormData = LabRequestBaseFormData & { request_text?: string | null };

// ---------------------------------------------------------------------------
// Toxicology
// ---------------------------------------------------------------------------

export type IntoxicationSuspicion = 'NE' | 'ANO' | 'NEJEDNOZNACNE' | '' | null;

type ToxicologyFormData_nested = {
  mechanism_of_death: {
    sudden_death: boolean;
    suicide: boolean;
    traffic_accident: boolean;
    murder: boolean;
    work_injury: boolean;
    other_injury: boolean;
    other: boolean;
  };
  body_changes: {
    advanced_autolysis: boolean;
    putrefaction: boolean;
    burn: boolean;
    other: boolean;
  };
  material: {
    femoral_blood: { enabled: boolean; amount: number | null };
    spleen_blood: { enabled: boolean; amount: number | null };
    heart_cavity_blood: { enabled: boolean; amount: number | null };
    subclavian_blood: { enabled: boolean; amount: number | null };
    bladder_wash: { enabled: boolean };
    other_objects: { enabled: boolean; text: string | null };
    organs: { enabled: boolean };
    vitreous: { enabled: boolean };
    stomach_content: { enabled: boolean };
    hair: { enabled: boolean };
    bile: { enabled: boolean };
    urine: { enabled: boolean; amount: number | null };
  };
  required_tests: {
    by_measures: boolean;
    ethanol: boolean;
    extractive: boolean;
    heavy_metals: boolean;
    cohb: boolean;
    just_sample: boolean;
    other: boolean;
    other_text: string | null;
  };
};

export type ToxicologyRequest = LabRequestBase & {
  intoxication_suspicion: IntoxicationSuspicion;
  circumstances_note: string;
  note: string;
  form_data: ToxicologyFormData_nested;
};

export type ToxicologyRequestFormData = LabRequestBaseFormData & {
  intoxication_suspicion?: IntoxicationSuspicion;
  circumstances_note?: string;
  note?: string;
  form_data?: ToxicologyFormData_nested;
};

// ---------------------------------------------------------------------------
// Tryptase
// ---------------------------------------------------------------------------

export type TryptaseRequest = LabRequestBase & {
  do_request: boolean;
};

export type TryptaseRequestFormData = LabRequestBaseFormData & {
  do_request?: boolean;
};

// ---------------------------------------------------------------------------
// Microbiology
// ---------------------------------------------------------------------------

type EnabledLocalization = { enabled: boolean; localization: string };
type EnabledText = { enabled: boolean; text: string };
type MycologyCultivation = { enabled: boolean; material: string };
type BloodstreamBottleCounts = {
  aerobic: number | null;
  anaerobic: number | null;
  pediatric: number | null;
};

type HcdCultivationType = 'general' | 'diphtheria' | 'bordetella';
type DcdCultivationType = 'general' | 'legionella' | 'bordetella';
type UrogenitalStiCultivationType = 'general' | 'go' | 'gbs';
type CollectionSourceType = 'peripheral_blood' | 'central_blood' | 'other_sterile';
type SideType = 'L' | 'R' | 'both';

type MicrobiologyFormData = {
  pus_fluid_tissue: {
    pus: EnabledLocalization;
    abscess_cyst: EnabledLocalization;
    furuncle_carbuncle: boolean;
    effusion: EnabledLocalization;
    biopsy_tissue: EnabledLocalization;
    joint_puncture: boolean;
    ascites: boolean;
  };
  git: {
    rectal_swab_enteric_pathogens: boolean;
    cholera_swab: boolean;
    rectal_swab_esbl_kpc_vre: boolean;
    bile: boolean;
    appendix_swab: boolean;
    clostridioides_difficile: boolean;
    stool_viruses: boolean;
    helicobacter_pylori: boolean;
  };
  hcd: {
    cultivation_type: HcdCultivationType | null;
    throat_tonsils_nasopharynx: boolean;
    nose: boolean;
    oral_mucosa_tongue_swab: boolean;
    paranasal_sinuses: boolean;
    peritonsillar_abscess: boolean;
    sars_cov2_antigen_nasopharynx: boolean;
  };
  dcd: {
    cultivation_type: DcdCultivationType | null;
    sputum: boolean;
    bal_aspirate: boolean;
    tracheostomy_swab: boolean;
    thoracic_puncture: boolean;
    legionella_antigen_urine: boolean;
    pneumococcus_antigen_urine: boolean;
    other: EnabledText;
  };
  urogenital_sti: {
    cultivation_type: UrogenitalStiCultivationType | null;
    vaginal_swab: boolean;
    rectal_swab: boolean;
    urethral_swab: boolean;
    throat_swab: boolean;
    cervical_swab: boolean;
    prostatic_secretion: boolean;
    vaginorectal_swab: boolean;
    mop_two_slides: boolean;
    gonorrhea_smear: boolean;
    trichomonas_smear: boolean;
    iud: boolean;
  };
  lesion_sample: {
    anaerobic: boolean;
    leg_ulcer_swab: boolean;
    surgical_wound_swab: boolean;
    traumatic_wound_swab: boolean;
    fistula_swab: boolean;
    skin_swab: boolean;
    decubitus_swab: boolean;
  };
  urine: {
    midstream: boolean;
    nephrostomy_epicystostomy: boolean;
    permanent_catheter: boolean;
  };
  cns: {
    csf: boolean;
    streptococcus_pneumoniae_antigen: boolean;
    aspergillus_antigen: boolean;
    cryptococcus_antigen: boolean;
  };
  ear: {
    side: SideType | null;
    external_auditory_canal_swab: boolean;
    middle_ear_content: boolean;
    other: EnabledText;
  };
  eye: {
    side: SideType | null;
    conjunctival_swab: boolean;
    other: EnabledText;
  };
  neonatology: {
    amniotic_fluid: boolean;
    ear_swab: boolean;
    umbilicus_swab: boolean;
    axilla_swab: boolean;
    gastric_content: boolean;
    other: EnabledText;
  };
  pathology: {
    liver: boolean;
    stomach: boolean;
    spleen: boolean;
    lung: boolean;
    small_intestine: boolean;
    lymph_node: boolean;
    large_intestine: boolean;
    other: EnabledText;
  };
  sterility: {
    environment_swab: boolean;
    fluid: boolean;
    other: EnabledText;
  };
  foreign_material: {
    drain: boolean;
    cannula: boolean;
    redon: boolean;
    vascular_catheter: boolean;
  };
  tbc_cultivation: {
    enabled: boolean;
    material: string;
  };
  mrsa_screening: {
    throat: boolean;
    nose: boolean;
    urine: boolean;
    hairline: boolean;
    perineum: boolean;
    wound: boolean;
  };
  bloodstream_bactec: {
    collection_source: CollectionSourceType | null;
    other_sterile_text: string;
    bottle_counts: BloodstreamBottleCounts;
    extended_cultivation_hacek: boolean;
    sequencing: boolean;
    other_request: EnabledText;
  };
  mycology: {
    cultivation: MycologyCultivation;
    panfungal_antigen_bd_glucan: boolean;
    galactomannan_aspergillus: boolean;
    cryptococcus_antigen: boolean;
    pcr_multiplex_dermatophyta: boolean;
  };
  pcr: {
    herpetic: {
      hsv1_hsv2: boolean;
      hsv1_hsv2_vzv: boolean;
      cmv_quantitative: boolean;
      ebv_quantitative: boolean;
      hhv6_7: boolean;
      hhv8: boolean;
    };
    respiratory: {
      sars_cov2_flu_rsv: boolean;
      sars_cov2_flu_rsv_gxp: boolean;
      multiplex_cap: boolean;
      multiplex_resp_panel: boolean;
      multiplex_pneumonia_plus: boolean;
      mycobacterium_tbc_complex: boolean;
      mycobacterium_tbc_complex_gxp: boolean;
      atypical_mycobacteria: boolean;
      strain_identification: boolean;
      aspergillus_spp: boolean;
      pneumocystis_jirovecii: boolean;
    };
    pediatric_exanthematic: {
      parvovirus_b19: boolean;
      parotitis: boolean;
      morbilli: boolean;
    };
    viral_hepatitides: {
      hbv_quantitative: boolean;
      hcv_quantitative: boolean;
      hcv_genotyping: boolean;
      hev: boolean;
    };
    sti: {
      chlamydia_trachomatis: boolean;
      chlamydia_trachomatis_gxp: boolean;
      neisseria_gonorrhoeae: boolean;
      neisseria_gonorrhoeae_gxp: boolean;
      monkeypox: boolean;
      multiplex_panel_sti: boolean;
      multiplex_panel_gu: boolean;
    };
    neuroinfections: {
      enteroviruses: boolean;
      borrelia: boolean;
      multiplex_meningitis_viruses: boolean;
      multiplex_meningitis_bacteria: boolean;
      multiplex_meningitis: boolean;
    };
    joint_implants: {
      multiplex_joints: boolean;
    };
    travel_infections: {
      multiplex_tropical_fevers: boolean;
      malaria: boolean;
      multiplex_protozoa: boolean;
      multiplex_helminths: boolean;
    };
    other: {
      multiplex_tick_borne: boolean;
      multiplex_dermatophyta: boolean;
      pcr_tularemia: boolean;
    };
  };
};

export type MicrobiologyRequest = LabRequestBase & {
  note: string;
  form_data: MicrobiologyFormData;
};

export type MicrobiologyRequestFormData = LabRequestBaseFormData & {
  note?: string;
  form_data?: MicrobiologyFormData;
};

// ---------------------------------------------------------------------------
// Genetics
// ---------------------------------------------------------------------------

export type GeneticsRequest = LabRequestBase & {
  pcr: boolean | null;
  medical: boolean | null;
};

export type GeneticsRequestFormData = LabRequestBaseFormData & {
  pcr?: boolean | null;
  medical?: boolean | null;
};

// ---------------------------------------------------------------------------
// Biochemistry
// ---------------------------------------------------------------------------

type BiochemistryBloodClotted = {
  natrium: boolean;
  kalium: boolean;
  chlorides: boolean;
  calcium_total: boolean;
  phosphorus: boolean;
  magnesium: boolean;
  glucose: boolean;
  urea: boolean;
  creatinine: boolean;
  uric_acid: boolean;
  bilirubin: boolean;
  bilirubin_conjugated: boolean;
  ast: boolean;
  alt: boolean;
  alp: boolean;
  ggt: boolean;
  ck: boolean;
  ldh: boolean;
  ches: boolean;
  ams: boolean;
  lipase: boolean;
  bile_acids: boolean;
  ace: boolean;
  osmolality: boolean;
  cystatin_c: boolean;
  crp: boolean;
  cholesterol: boolean;
  hdl_cholesterol: boolean;
  ldl_cholesterol: boolean;
  triacylglycerols: boolean;
  apolipoprotein_a1: boolean;
  apolipoprotein_b: boolean;
  lipoprotein_a: boolean;
  total_protein: boolean;
  albumin: boolean;
  beta_2_microglobulin: boolean;
  prealbumin: boolean;
  transferrin: boolean;
  transferrin_saturation: boolean;
  ferritin: boolean;
  iron: boolean;
  iron_binding_capacity: boolean;
  vitamin_b12: boolean;
  vitamin_b12_active: boolean;
  folate: boolean;
  hs_troponin_i: boolean;
  myoglobin: boolean;
  nt_probnp: boolean;
  digoxin: boolean;
  theophylline: boolean;
  afp: boolean;
  cea: boolean;
  ca_15_3: boolean;
  ca_19_9: boolean;
  ca_125: boolean;
  ca_72_4: boolean;
  hcg_beta: boolean;
  psa_total: boolean;
  psa_free: boolean;
  scca: boolean;
  s100b: boolean;
  nse: boolean;
  tsh: boolean;
  t4_total: boolean;
  t3_total: boolean;
  t4_free: boolean;
  t3_free: boolean;
  anti_tg: boolean;
  anti_tpo: boolean;
  anti_tsh_receptor_trak: boolean;
  cortisol: boolean;
  estradiol: boolean;
  progesterone: boolean;
  prolactin: boolean;
  lh: boolean;
  fsh: boolean;
  testosterone: boolean;
  dhea_s: boolean;
  shbg: boolean;
  c_peptide: boolean;
  vitamin_d_25_oh: boolean;
  phenytoin: boolean;
  valproic_acid: boolean;
  lithium: boolean;
  interleukin_6: boolean;
  procalcitonin_pct: boolean;
  plgf: boolean;
  igf_1: boolean;
};

type BiochemistryBloodUnclotted = {
  blood_count: boolean;
  blood_count_diff_analyzer: boolean;
  blood_count_reticulocytes: boolean;
  blood_count_diff_reticulocytes: boolean;
  immature_platelet_fraction: boolean;
  differential_microscopy: boolean;
  platelets_microscopy: boolean;
  schistocytes: boolean;
};

type BiochemistryBloodK3Edta = {
  hba1c: boolean;
  homocysteine: boolean;
  parathormone: boolean;
};

type BiochemistryBloodNafEdta = {
  glucose: boolean;
  ogtt: boolean;
  ogtt_pregnant: boolean;
  lactate: boolean;
};

type BiochemistryBloodCitrate = {
  prothrombin_test_quick: boolean;
  aptt: boolean;
  thrombin_test: boolean;
  d_dimers: boolean;
  fibrinogen: boolean;
  antithrombin: boolean;
  fdp: boolean;
  fm: boolean;
  anticoagulation_treatment: string;
  protein_c: boolean;
  protein_s: boolean;
  apc_resistance: boolean;
  factor_viii: boolean;
  factor_ix: boolean;
  factor_xii: boolean;
  lupus_anticoagulans: boolean;
  anti_xa_lmwh: boolean;
  anti_xa_ufh: boolean;
  arixtra: boolean;
  apixaban: boolean;
  rivaroxaban: boolean;
  acetylsalicylic_acid_asa: boolean;
  clopidogrel_prasugrel_ticlopidine_ticagrelor: boolean;
};

type BiochemistryBloodClottedSeparate = {
  blood_group_rh_factor: boolean;
  antibody_screening: boolean;
  direct_coombs_test: boolean;
};

type BiochemistryUrineSingle = {
  urine_chemistry_morphology: boolean;
  amylase: boolean;
  cb_creatinine: boolean;
  albumin_creatinine: boolean;
  osmolality: boolean;
  beta_2_microglobulin: boolean;
};

type BiochemistryFormData_nested = {
  blood_clotted: BiochemistryBloodClotted;
  blood_unclotted: BiochemistryBloodUnclotted;
  blood_k3edta: BiochemistryBloodK3Edta;
  blood_naf_edta: BiochemistryBloodNafEdta;
  blood_citrate: BiochemistryBloodCitrate;
  blood_clotted_separate: BiochemistryBloodClottedSeparate;
  urine_single: BiochemistryUrineSingle;
};

export type BiochemistryRequest = LabRequestBase & {
  primary_sample: string | null;
  form_data: BiochemistryFormData_nested;
};

export type BiochemistryRequestFormData = LabRequestBaseFormData & {
  primary_sample?: string | null;
  form_data?: BiochemistryFormData_nested;
};

// ---------------------------------------------------------------------------
// Histology
// ---------------------------------------------------------------------------

export type CnsMaterialChoice =
  | 'brain'
  | 'cerebellum'
  | 'brainstem'
  | 'brain_dura_mater'
  | 'spinal_cord';
export type BoneMarrowChoice = 'vertebra' | 'sternum' | 'rib' | 'femur';
export type SideChoiceValue = 'right' | 'left';
export type UterusChoiceValue = 'cervix' | 'cavity';

type CnsMaterial = { enabled: boolean; choices: CnsMaterialChoice[] };
type BoneMarrowMaterial = { enabled: boolean; choices: BoneMarrowChoice[] };
type MaterialWithText = { enabled: boolean; text: string | null };
export type SideChoice = { enabled: boolean; choices: SideChoiceValue[] };
export type UterusChoice = { enabled: boolean; choices: UterusChoiceValue[] };

type HistologyFixedMaterial = {
  cns: CnsMaterial;
  nasal_septum: boolean;
  epiglottis: boolean;
  larynx: boolean;
  thyroid_gland: boolean;
  trachea: boolean;
  lungs: boolean;
  heart: boolean;
  thymus: boolean;
  liver: boolean;
  spleen: boolean;
  stomach: boolean;
  small_intestine: boolean;
  large_intestine_colon: boolean;
  pancreas: boolean;
  adrenal_glands: boolean;
  kidneys: boolean;
  prostate: boolean;
  eyeballs_and_optic_nerves: boolean;
  pituitary_gland: boolean;
  bone_marrow: BoneMarrowMaterial;
  other: MaterialWithText[];
};

export type HistologyRow = { tissue: string; method: string };

type HistologyNotes = {
  sectioning_physician: MaterialWithText;
  whole_organ_fixation: string | null;
  other: string | null;
};

type Cytology = {
  nasal_cavity: SideChoice;
  oral_cavity: boolean;
  vulva: boolean;
  vagina: boolean;
  uterus: UterusChoice;
  rectum: boolean;
  vitreous: SideChoice;
  other: MaterialWithText[];
  note: string | null;
};

type HistologyFormData_nested = {
  histology_fixed: HistologyFixedMaterial;
  special_staining: HistologyRow[];
  frozen_material: HistologyRow[];
  histology_notes: HistologyNotes;
  cytology: Cytology;
};

export type HistologyRequest = LabRequestBase & {
  form_data: HistologyFormData_nested;
  macroscopic_heart_histochemistry: boolean;
  requesting_physician: number;
};

export type HistologyRequestFormData = LabRequestBaseFormData & {
  form_data?: HistologyFormData_nested;
  macroscopic_heart_histochemistry?: boolean;
  requesting_physician?: number | null;
};

// ---------------------------------------------------------------------------
// NRL Other
// ---------------------------------------------------------------------------

export type NrlOtherRequest = LabRequestBase & {
  swab_nose_throat_collected_at: string | null;
  swab_nose_throat_note: string;
  endotracheal_swab_aspirate_sputum_collected_at: string | null;
  endotracheal_swab_aspirate_sputum_note: string;
  bal_collected_at: string | null;
  bal_note: string;
  csf_collected_at: string | null;
  csf_note: string;
  biopsy_collected_at: string | null;
  biopsy_note: string;
  full_blood_clot_collected_at: string | null;
  full_blood_clot_note: string;
  saliva_collected_at: string | null;
  saliva_note: string;
  autopsy_swabs_collected_at: string | null;
  autopsy_swabs_note: string;
  other_material_name: string;
  other_collected_at: string | null;
  other_note: string;
};

export type NrlOtherRequestFormData = LabRequestBaseFormData &
  Partial<Omit<NrlOtherRequest, keyof LabRequestBase>>;

// ---------------------------------------------------------------------------
// NRL Respiratory
// ---------------------------------------------------------------------------

type NrlSymptomBasic = { present: boolean; days: number | null };
type NrlSymptomWithTemp = { present: boolean; days: number | null; value_c: number | null };
type NrlSymptomOther = { present: boolean; days: number | null; text: string | null };

type NrlRespiratorySymptoms = {
  temperature: NrlSymptomWithTemp;
  malaise: NrlSymptomBasic;
  runny_nose: NrlSymptomBasic;
  cough: NrlSymptomBasic;
  dyspnea: NrlSymptomBasic;
  headache: NrlSymptomBasic;
  chest_pain: NrlSymptomBasic;
  abdominal_pain: NrlSymptomBasic;
  muscle_pain: NrlSymptomBasic;
  vomiting: NrlSymptomBasic;
  diarrhea: NrlSymptomBasic;
  tonsillitis: NrlSymptomBasic;
  conjunctivitis: NrlSymptomBasic;
  tracheitis: NrlSymptomBasic;
  bronchitis: NrlSymptomBasic;
  exanthem: NrlSymptomBasic;
  loss_smell_taste: NrlSymptomBasic;
  other: NrlSymptomOther;
};

type YesNoToggle = { enabled: boolean; disabled: boolean };

type NrlSariTreatment = {
  enabled: boolean;
  disabled: boolean;
  from_date: string | null;
  text: string | null;
};

type NrlSariPregnancy = {
  enabled: boolean;
  disabled: boolean;
  text: string | null;
};

type NrlRespiratorySari = {
  treatment: NrlSariTreatment;
  atb: YesNoToggle;
  antivirals: YesNoToggle;
  obesity: YesNoToggle;
  respiratory_support: YesNoToggle;
  ecmo: YesNoToggle;
  pregnancy: NrlSariPregnancy;
  flu_vaccination: YesNoToggle;
  vaccine_name_text: string | null;
  vaccine_batch_text: string | null;
};

type NrlExamItem = { pcr: boolean; confirmation: boolean; nrl_seq_lt28: boolean };
type NrlExamOther = {
  pcr: boolean;
  confirmation: boolean;
  nrl_seq_lt28: boolean;
  text: string | null;
};

type NrlRespiratoryExamination = {
  influenza: NrlExamItem;
  sarscov2: NrlExamItem;
  rsv_metapneumo: NrlExamItem;
  parainfl_corona: NrlExamItem;
  rhino_entero: NrlExamItem;
  adeno_boca: NrlExamItem;
  parecho: NrlExamItem;
  mers: NrlExamItem;
  chlam_mycopl: NrlExamItem;
  legionella: NrlExamItem;
  other: NrlExamOther;
};

type DatedMaterial = { taken_at: string | null; note: string | null };
type NrlMaterialOther = { taken_at: string | null; note: string | null; text: string | null };

type NrlRespiratoryMaterial = {
  swab_nose_throat: DatedMaterial;
  endotracheal_swab: DatedMaterial;
  bal: DatedMaterial;
  sputum_or_aspirate: DatedMaterial;
  csf: DatedMaterial;
  biopsy: DatedMaterial;
  full_blood_clot: DatedMaterial;
  saliva: DatedMaterial;
  other: NrlMaterialOther;
};

type NrlRespiratoryFormData_nested = {
  symptoms: NrlRespiratorySymptoms;
  sari: NrlRespiratorySari;
  examination: NrlRespiratoryExamination;
  material: NrlRespiratoryMaterial;
};

export type NrlRespiratoryRequest = LabRequestBase & {
  important_symptoms_note: string | null;
  focus_note: string | null;
  form_data: NrlRespiratoryFormData_nested;
};

export type NrlRespiratoryRequestFormData = LabRequestBaseFormData & {
  important_symptoms_note?: string | null;
  focus_note?: string | null;
  form_data?: NrlRespiratoryFormData_nested;
};

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

type TransportFormData_nested = {
  reason: { text: string };
  origin: {
    use_provider_department: boolean;
    name_override: string;
    address_override: string;
    paid_by_insurance: boolean;
  };
};

export type TransportRequest = {
  id: number;
  autopsy_protocol: number;
  examination_type: number;
  examination_type_code: string;
  created_by: number | null;
  created_by_name: string;
  requesting_department: number;
  requesting_department_icp: string;
  requesting_department_specialization_code: string;
  requesting_department_name: string;
  requesting_organization_name: string;
  deceased_name: string;
  deceased_insurance_number: string;
  deceased_insurance_company_code: string;
  transport_diagnoses: { code: string; name: string }[];
  last_printed_at: string | null;
  sent_at: string | null;
  form_data: TransportFormData_nested;
};

export type TransportRequestFormData = {
  autopsy_protocol: number;
  examination_type: number;
  requesting_department: number;
  form_data?: TransportFormData_nested;
};

// ---------------------------------------------------------------------------
// Virology
// ---------------------------------------------------------------------------

export type VirologyFormData = {
  bacterial_serology: {
    tularemia_antibodies: boolean;
    brucellosis_antibodies: boolean;
    toxoplasmosis_kfr_igg_igm_iga_ige: boolean;
    yersinia_species_igg_iga_wb: boolean;
    widal_salmonella_antibodies: boolean;
    borreliosis_igg_igm_elisa_wb: boolean;
    cxcl13_csf: boolean;
    antibody_index_csf_blood: boolean;
  };
  mycology: {
    cryptococcus_antigenemia: boolean;
    aspergillus_antigenemia: boolean;
  };
  mycobacteriology: {
    quantiferon_tb: boolean;
    t_spot_tb: boolean;
  };
  congenital_infections: {
    toxoplasma_gondii_kfr_igm_iga_igg: boolean;
    rubella_virus: boolean;
    herpetic_viruses: boolean;
    parvovirus_b19: boolean;
  };
  childhood_exanthem_infections: {
    rubella_virus_igm_igg: boolean;
    morbillivirus_igm_igg: boolean;
    parvovirus_b19_igm_igg: boolean;
    mumps_virus_igm_igg: boolean;
  };
  traveler_infections: {
    chikungunya_virus_igm_igg: boolean;
    dengue_virus_igm_igg: boolean;
    dengue_virus_ns1_ag: boolean;
    zika_virus_igm_igg: boolean;
    hantaviruses_igm_igg_typing: boolean;
    japanese_encephalitis_virus_igm_igg: boolean;
    west_nile_virus_igm_igg: boolean;
  };
  respiratory_infections: {
    respiratory_block: boolean;
    influenza_a_b_kfr_igm: boolean;
    adenoviruses_kfr_igm: boolean;
    rsv_kfr_igm_iga: boolean;
    mycoplasma_pneumoniae_kfr_igm_iga: boolean;
    parainfluenza_1_3_iga_igg: boolean;
    legionella_pneumophila_igm_igg: boolean;
    bordetella_pertussis_iga_igg: boolean;
    bordetella_parapertussis_iga_igg: boolean;
    sars_cov_2_blok: boolean;
    sars_cov_2_antibodies_igm_iga: boolean;
    sars_cov_2_spike_protein_igg: boolean;
    sars_cov_2_n_antigen_blood: boolean;
    sars_cov_2_antigen_nasopharyngeal_swab: boolean;
  };
  atypical_pneumonia: {
    mycoplasma_pneumoniae_kfr_igm_iga_igg: boolean;
    chlamydia_lps_igm_iga_igg: boolean;
    chlamydia_pneumoniae_psittaci_igm_iga_igg: boolean;
  };
  neuroinfections: {
    tick_borne_meningoencephalitis_virus: boolean;
    enteroblock_kfr_igm_iga_igg: boolean;
    neuroborreliosis_cxcl13: boolean;
    neurosyphilis_cxcl13: boolean;
  };
  herpetic_infections: {
    herpetic_block: boolean;
    hsv_1_2_igm_igg_kfr: boolean;
    cmv_igm_igg_igg_avidity_kfr: boolean;
    vzv_igm_igg_kfr: boolean;
    ebv_igm_igg_anti_ea_vca_ebna: boolean;
    hhv6_igm_igg: boolean;
  };
  rickettsiae: {
    bartonella_igm_igg: boolean;
    rickettsia_conorii_igm_igg: boolean;
    coxiella_burnetii_igm_iga_igg_kfr: boolean;
  };
  sexually_transmitted_infections: {
    syphilis_serology_rpr_tppa: boolean;
    hiv_1_2_p24_antigen: boolean;
    chlamydia_trachomatis_igm_iga_igg: boolean;
    mycoplasma_hominis_igm_igg: boolean;
    ureaplasma_urealyticum_igm_igg: boolean;
  };
  viral_hepatitis: {
    hepatitis_panel_a_b_c: boolean;
    hepatitis_a: boolean;
    anti_hav_igm: boolean;
    anti_hav_ig_total: boolean;
    hepatitis_b: boolean;
    hepatitis_b_hbsag_quantitative: boolean;
    hepatitis_b_hbsag_confirmation: boolean;
    hepatitis_b_hbeag_quantitative: boolean;
    hepatitis_b_anti_hbe_igg: boolean;
    hepatitis_b_anti_hbs_quantitative: boolean;
    hepatitis_b_anti_hbc_ig_total: boolean;
    hepatitis_b_anti_hbc_igm: boolean;
    hepatitis_c: boolean;
    anti_hcv_ig_total: boolean;
    anti_hcv_confirmation_wb: boolean;
    hepatitis_e: boolean;
    anti_hev_igg_igm: boolean;
    anti_hev_confirmation_wb: boolean;
  };
  post_vaccination_igg_check: {
    tick_borne_encephalitis_virus_quantitative: boolean;
    poliovirus_1_2_3: boolean;
    vzv: boolean;
    rubella_virus_quantitative: boolean;
    morbillivirus: boolean;
    mumps_virus: boolean;
    tetanus: boolean;
    hepatitis_b: boolean;
    sars_cov_2_spike_protein: boolean;
  };
  direct_antigen_detection: {
    respiratory_infections: {
      direct_sars_cov_2_antigen_nasopharyngeal_swab: boolean;
    };
    git_infections_stool: {
      rotaviruses: boolean;
      adenoviruses: boolean;
      astroviruses: boolean;
      noroviruses_genotype_1_2: boolean;
      enteroviruses: boolean;
    };
  };
};

export type VirologyRequest = LabRequestBase & {
  form_data: VirologyFormData;
};

export type VirologyRequestFormData = LabRequestBaseFormData & {
  form_data?: VirologyFormData;
};

// ---------------------------------------------------------------------------
// API factory
// ---------------------------------------------------------------------------

const printRequest = (endpoint: string) => ({
  print: (id: number) => axios.get<Blob>(`${endpoint}${id}/print/`, { responseType: 'blob' })
});

const getCurrentRequest = <T>(endpoint: string) => ({
  getCurrent: (autopsyProtocolId: number, examinationTypeId?: number) =>
    axios.get<T>(`${endpoint}current/`, {
      params: {
        autopsy_protocol: autopsyProtocolId,
        ...(examinationTypeId ? { examination_type: examinationTypeId } : {})
      }
    })
});

export const LabRequestsApi = () => ({
  ct: {
    ...createCrudApi<CtRequest, CtRequestFormData, LabRequestsGetParams>('/v1/lab-requests/ct/'),
    ...printRequest('/v1/lab-requests/ct/'),
    ...getCurrentRequest<CtRequest>('/v1/lab-requests/ct/')
  },
  rtg: {
    ...createCrudApi<RtgRequest, RtgRequestFormData, LabRequestsGetParams>('/v1/lab-requests/rtg/'),
    ...printRequest('/v1/lab-requests/rtg/'),
    ...getCurrentRequest<RtgRequest>('/v1/lab-requests/rtg/')
  },
  toxicology: {
    ...createCrudApi<ToxicologyRequest, ToxicologyRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/toxicology/'
    ),
    ...printRequest('/v1/lab-requests/toxicology/'),
    ...getCurrentRequest<ToxicologyRequest>('/v1/lab-requests/toxicology/')
  },
  tryptase: {
    ...createCrudApi<TryptaseRequest, TryptaseRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/tryptase/'
    ),
    ...printRequest('/v1/lab-requests/tryptase/'),
    ...getCurrentRequest<TryptaseRequest>('/v1/lab-requests/tryptase/')
  },
  microbiology: {
    ...createCrudApi<MicrobiologyRequest, MicrobiologyRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/microbiology/'
    ),
    ...printRequest('/v1/lab-requests/microbiology/'),
    ...getCurrentRequest<MicrobiologyRequest>('/v1/lab-requests/microbiology/')
  },
  genetics: {
    ...createCrudApi<GeneticsRequest, GeneticsRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/genetics/'
    ),
    ...printRequest('/v1/lab-requests/genetics/'),
    ...getCurrentRequest<GeneticsRequest>('/v1/lab-requests/genetics/')
  },
  biochemistry: {
    ...createCrudApi<BiochemistryRequest, BiochemistryRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/biochemistry/'
    ),
    ...printRequest('/v1/lab-requests/biochemistry/'),
    ...getCurrentRequest<BiochemistryRequest>('/v1/lab-requests/biochemistry/')
  },
  histology: {
    ...createCrudApi<HistologyRequest, HistologyRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/histology/'
    ),
    ...printRequest('/v1/lab-requests/histology/'),
    ...getCurrentRequest<HistologyRequest>('/v1/lab-requests/histology/')
  },
  nrlOther: {
    ...createCrudApi<NrlOtherRequest, NrlOtherRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/nrl-other/'
    ),
    ...printRequest('/v1/lab-requests/nrl-other/'),
    ...getCurrentRequest<NrlOtherRequest>('/v1/lab-requests/nrl-other/')
  },
  nrlRespiratory: {
    ...createCrudApi<NrlRespiratoryRequest, NrlRespiratoryRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/nrl-respiratory/'
    ),
    ...printRequest('/v1/lab-requests/nrl-respiratory/'),
    ...getCurrentRequest<NrlRespiratoryRequest>('/v1/lab-requests/nrl-respiratory/')
  },
  transport: {
    ...createCrudApi<TransportRequest, TransportRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/transport/'
    ),
    ...printRequest('/v1/lab-requests/transport/'),
    ...getCurrentRequest<TransportRequest>('/v1/lab-requests/transport/')
  },
  virology: {
    ...createCrudApi<VirologyRequest, VirologyRequestFormData, LabRequestsGetParams>(
      '/v1/lab-requests/virology/'
    ),
    ...printRequest('/v1/lab-requests/virology/'),
    ...getCurrentRequest<VirologyRequest>('/v1/lab-requests/virology/')
  }
});
